import React from 'react';
import { makeStyles, Typography, ButtonBase, Grid } from '@material-ui/core';
import clsx from 'clsx';
import { Check } from 'react-feather';
import DefaultCard from '../DefaultCard';
import DefaultCardRow from '../DefaultCardRow';
import colors from '../../../theme/colors';
import { DisplayItem } from '../../../types';

const iconDiameter = 24;

const useStyles = makeStyles({
  container: {
    display: 'block',
    padding: '4px 0',
    margin: 0,
    width: '100%',
  },
  baseMark: {
    display: 'inline-block',
    width: iconDiameter,
    height: iconDiameter,
    marginRight: 8,
    borderRadius: iconDiameter / 2.0,
  },
  baseMarkContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: iconDiameter,
    height: iconDiameter,
  },
  markUnchecked: {
    backgroundColor: 'transparent',
    border: `1px solid ${colors.bright_turquoise}`,
  },
  markChecked: {
    backgroundColor: colors.bright_turquoise,
  },
});

type SelectionCardProps = {
  /** The card title (basically, the call to action) */
  title: React.ReactNode;
  /** The options to display */
  options: DisplayItem[];
  /** Called when a new option is selected */
  onUpdateCurrentOption: (newOption: DisplayItem) => void;
  /** The current option's Value property */
  currentOption?: string;
  /** Additional content */
  additionalContent?: React.ReactNode;
  inputId?: string;
};

const SelectionCard = ({
  title,
  options,
  onUpdateCurrentOption,
  currentOption = '',
  additionalContent,
  inputId,
}: SelectionCardProps): JSX.Element => {
  const classes = useStyles();

  return (
    <DefaultCard title={title}>
      {/* Small separator */}
      <div style={{ height: 5 }} />
      {options.map(
        (option: DisplayItem, index: number): React.ReactNode => (
          <ButtonBase
            id={inputId}
            data-testid={option.Value}
            key={option.Value}
            disableRipple
            className={classes.container}
            onClick={(): void => onUpdateCurrentOption(option)}
          >
            <DefaultCardRow>
              <Grid container alignItems="center">
                <div
                  id={`${inputId}-${index}`}
                  data-testid={`option-mark-${option.Value}`}
                  className={clsx(
                    classes.baseMark,
                    option.Value === currentOption
                      ? classes.markChecked
                      : classes.markUnchecked,
                  )}
                >
                  {option.Value === currentOption && (
                    <div className={classes.baseMarkContent}>
                      <Check color={colors.white} size={16} />
                    </div>
                  )}
                </div>
                {typeof option.Text === 'string' ? (
                  <Typography variant="caption">{option.Text}</Typography>
                ) : (
                  option.Text
                )}
              </Grid>
            </DefaultCardRow>
          </ButtonBase>
        ),
      )}
      {additionalContent}
    </DefaultCard>
  );
};

export default SelectionCard;
